import { useIntl } from 'react-intl';
import { ButtonUI, EmptyState } from 'sonata-react-components';

import { BoxGroup } from '@/components/Targeting/AudienceTargeting/AudienceTargetingGroupList/BoxGroup';
import GroupSkeleton from '../AudienceTargetingList/ListSkeleton/GroupSkeleton';
import { AudienceTargetingGroupListStyled } from './AudienceTargetingGroupList.styled';

import type { RetargetingSegmentType } from 'sonata-static-storage/lib/schemas/targeting/Audience/Audience.types';
import type { AudienceGroup } from '../AudienceTargeting';

type Props = {
  groupsList: AudienceGroup[];
  isLoading: boolean;
  onDeleteGroupChange: (id: string) => void;
  onDeleteRetargetingSegment?: () => void;
  onDeleteSegmentChange: (id: string, groupId: string) => void;
  onNewGroupChange: () => void;
  onSelectedGroup: (id: string) => void;
  retargetingSegment?: RetargetingSegmentType | null;
  selectedGroup: string | null;
};

function AudienceTargetingGroupList({
  groupsList,
  isLoading,
  onDeleteGroupChange,
  onDeleteRetargetingSegment,
  onDeleteSegmentChange,
  onNewGroupChange,
  onSelectedGroup,
  retargetingSegment,
  selectedGroup
}: Props) {
  const { formatMessage } = useIntl();

  return (
    <AudienceTargetingGroupListStyled className="audienceTargetingGroupList">
      <div className="audienceTargetingGroupList__boxHeader">
        <span className="audienceTargetingGroupList__boxHeader--title">
          {formatMessage({
            defaultMessage: 'Your audience selection',
            id: 'targeting.audienceTargeting.audienceSettings.title'
          })}
        </span>
        {groupsList?.length >= 1 && (
          <ButtonUI
            disabled={
              groupsList.some(group => group.segmentIds.length === 0 && group.id !== retargetingSegment?.listId) ||
              isLoading
            }
            icon="fa-regular fa-plus"
            iconPosition="left"
            id="audienceTargetingGroupList-button"
            onClick={onNewGroupChange}
            shape="squareRounded"
            size="ultraSmall"
            text={formatMessage({
              defaultMessage: 'New group',
              id: 'targeting.audienceTargeting.audienceSettings.button'
            })}
            type="secondary"
          />
        )}
      </div>
      <div className="audienceTargetingGroupList__boxContainer">
        {isLoading ? (
          <GroupSkeleton />
        ) : groupsList?.length === 0 ? (
          <div className="audienceTargetingGroupList__emptyState">
            <EmptyState
              description={formatMessage({
                defaultMessage: 'Add an audience to unlock tailored insights and analytics.',
                id: 'targeting.audienceTargeting.audienceSettings.emptyState.description'
              })}
              fontSizes={{
                description: '12px',
                icon: '18px',
                title: '14px'
              }}
              icon="fal fa-empty-set"
              show
              spacing="small"
              title={formatMessage({
                defaultMessage: 'Get Started with Your Audience',
                id: 'targeting.audienceTargeting.audienceSettings.emptyState.title'
              })}
            />
          </div>
        ) : (
          groupsList?.map((group, i) => (
            <BoxGroup
              group={group}
              groupNumber={groupsList.filter(g => g.type === 'included').length - i}
              index={i}
              isGroupActive={group.id === selectedGroup}
              key={i}
              onChangeDeleteGroup={onDeleteGroupChange}
              onChangeDeleteRetargetingSegment={onDeleteRetargetingSegment}
              onChangeDeleteSegment={onDeleteSegmentChange}
              onSelectedGroup={onSelectedGroup}
              retargetingSegment={retargetingSegment?.listId === group.id ? retargetingSegment : null}
            />
          ))
        )}
      </div>
    </AudienceTargetingGroupListStyled>
  );
}

export default AudienceTargetingGroupList;
